import React from 'react';
import {connect} from 'react-redux';
import classNames from 'classnames';
import {push} from 'react-router-redux';

import Avatar from 'components/Avatar';
import LoginRequired from 'components/LoginRequired';
import IconButton from 'components/IconButton';
import ButtonDropdown from 'components/ButtonDropdown';
import VisibleIf from 'components/VisibleIf';
import Tooltip from 'components/Tooltip';
import {LocationSwitch} from './components';

import {
  selectCurrentUser,
  requiresPermission,
  PERMISSIONS,
  logoutRequest
} from 'ducks/auth';
import {exportCsv, selectExporting} from 'ducks/files';

import {exportEmergencyContactsCsv as exportEmergencyContactsCsvSdk} from 'sdk/locations';

import {
  MY_TEAMS,
  LOCATION_LIST_URL,
  USER_LIST_URL,
  WEBHOOKS_URL,
  QUICK_SEARCH_URL,
  STATUS_PAGE_URL
} from 'config/urls';
import {reverse} from 'utils/urls';

import logoutImage from './assets/logout.svg';
import emergencyContactsImage from './assets/emergency.svg';

import './styles.css';

const EMERGENCY_CONTACTS_CSV_FILENAME = 'emergency_contacts';
const TOOLTIP_NAME = 'navigationTooltip';

interface PassedProps {
  className?: string;
  locationRef?: string;
}

type NavigationProps = PassedProps &
  ReturnType<typeof mapStateToProps> &
  typeof mapDispatchToProps;

class Navigation extends React.Component<NavigationProps> {
  dropdownHeader = () => (
    <Avatar
      url={
        this.props.user.internalAvatarCropped || this.props.user.avatarCropped
      }
      initials={this.props.user.initials}
      width={64}
    />
  );

  logoutAction = () => {
    this.props.logoutRequest();
  };

  goToUsers = () => {
    return this.props.requiresPermission(
      {permission: PERMISSIONS.VIEW_USERS},
      () => this.props.push(USER_LIST_URL)
    );
  };

  exportEmergencyContactsCsv = () => {
    const {locationRef, exportingEmergencyContactsCsv} = this.props;

    if (!exportingEmergencyContactsCsv) {
      this.props.requiresPermission(
        {
          permission: PERMISSIONS.EXPORT_EMERGENCY_CONTACTS_LIST,
          locationRef
        },
        () =>
          this.props.exportCsv({
            exportData: {locationRef},
            exportSdk: exportEmergencyContactsCsvSdk,
            fileName: EMERGENCY_CONTACTS_CSV_FILENAME
          })
      );
    }
  };

  render() {
    const {className, locationRef} = this.props;

    return (
      <div
        className={classNames(
          className,
          'Navigation colab-centered-v colab-row-space-between'
        )}>
        <div className="colab-elements-row colab  -elements-row-centered-v">
          {locationRef && <LocationSwitch locationRef={locationRef} />}
        </div>
        <div className="colab-elements-row colab-elements-row-centered-v">
          <div
            className="ExportEmergencyContactsButton"
            data-tip="Export Emergency Contancts"
            data-for={TOOLTIP_NAME}>
            <IconButton
              src={emergencyContactsImage}
              alt="Export Emergency Contancts"
              onClick={this.exportEmergencyContactsCsv}
            />
          </div>

          <div
            className="LogoutButton"
            data-tip="Log out"
            data-for={TOOLTIP_NAME}>
            <IconButton
              src={logoutImage}
              alt="Logout"
              onClick={this.logoutAction}
            />
          </div>

          <ButtonDropdown
            HeaderComponent={this.dropdownHeader}
            menuPosition={ButtonDropdown.MENU_POSITION_BOTTOM_LEFT}
            className="Navigation__dropdown"
            dropdownClassName="Navigation__dropdown_menu">
            <ButtonDropdown.Option
              onClick={() => this.props.push(LOCATION_LIST_URL)}>
              Locations
            </ButtonDropdown.Option>
            <ButtonDropdown.Option onClick={() => this.props.push(MY_TEAMS)}>
              My Teams
            </ButtonDropdown.Option>
            <ButtonDropdown.Option onClick={this.goToUsers}>
              Users
            </ButtonDropdown.Option>
            <VisibleIf permission={PERMISSIONS.VIEW_QUICKSEARCH}>
              <ButtonDropdown.Option
                onClick={() => this.props.push(QUICK_SEARCH_URL)}>
                Quick Search
              </ButtonDropdown.Option>
            </VisibleIf>
            <VisibleIf permission={PERMISSIONS.VIEW_WEBHOOKS}>
              <ButtonDropdown.Option
                onClick={() => this.props.push(WEBHOOKS_URL)}>
                Webhooks
              </ButtonDropdown.Option>
            </VisibleIf>
            <VisibleIf
              permission={PERMISSIONS.VIEW_INTEGRATIONS_STATUS}
              locationRef={locationRef}>
              <ButtonDropdown.Option
                onClick={() =>
                  this.props.push(reverse(STATUS_PAGE_URL, {locationRef}))
                }>
                Integrations Status
              </ButtonDropdown.Option>
            </VisibleIf>
          </ButtonDropdown>
        </div>
        <Tooltip
          place="bottom"
          delayShow={300}
          effect="solid"
          id={TOOLTIP_NAME}
        />
      </div>
    );
  }
}

const mapStateToProps = state => {
  return {
    user: selectCurrentUser(state),
    exportingEmergencyContactsCsv: selectExporting(
      state,
      EMERGENCY_CONTACTS_CSV_FILENAME
    )
  };
};

const mapDispatchToProps = {logoutRequest, push, requiresPermission, exportCsv};

export default connect(mapStateToProps, mapDispatchToProps)(
  LoginRequired(Navigation)
);
